import { Colors, htmlToSlate, Icon, slateToHtml, type ListItemData } from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { type Descendant } from 'slate';

import type { CycleEvaluationFormNavigatorParams } from '@/navigation/CycleEvaluation/CycleEvaluationFormNavigator';
import type { LaunchReviewTranslationKeys } from '@/pages/shared/ScheduleCreation/components/LaunchReview/LaunchReview';
import type { MultilingualLabel } from '@/types/Locale';

import {
    useGetCycleEvaluation,
    useLaunchCycleEvaluation,
} from '@/api/cycle-evaluation-services/cycle-evaluation';
import { useUpdateCycleSchedule } from '@/api/cycle-evaluation-services/cycle-schedule';
import { useSendCycleEvaluationTestEmail } from '@/api/email';
import { scheduleCreationOnNextAtom } from '@/atoms/evaluation360';
import { userInstanceAtom } from '@/atoms/userDetails';
import { CycleEvaluationStatus } from '@/constants/cycle-evaluation/CycleEvaluation';
import { Frequency } from '@/constants/Frequency';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useError } from '@/hooks/utils/useError';
import { useRegisterPageAction } from '@/hooks/utils/useRegisterPageAction';
import { RouteName } from '@/navigation/types';
import { hasQuestionnaireContent } from '@/pages/CycleEvaluation/LaunchSummary/launchGuard';
import { ScheduleCreation } from '@/pages/shared/ScheduleCreation/components/ScheduleCreation';
import {
    ScheduleCreationFormFields,
    scheduleCreationSchema,
    type ScheduleCreationFormData,
} from '@/pages/shared/ScheduleCreation/validation';
import { SuccessMessageAction } from '@/pages/shared/SuccessMessage/SuccessMessagePage';
import i18n from '@/plugins/i18n';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/plugins/Toast';
import {
    MANAGER_NOTIFICATION_DEFAULT_TITLES,
    MANAGER_NOTIFICATION_DEFAULT_BODIES,
} from '@/utils/emailDefaultsManagerNotification';
import { getManagerNotificationEmailDefaults } from '@/utils/emailDefaultsManagerNotification';
import { getFrequencyOptions } from '@/utils/frequency';
import {
    getAvailableLanguages,
    normalizeLanguageCode,
    type LocalizedText,
} from '@/utils/localization';
import {
    getEmptyEmailMessage,
    isScheduleReminderChecked,
    mergeScheduleReminders,
} from '@/utils/schedule';
import { validationMessageKey } from '@/utils/validationMessage';

const ScheduleCreationPage = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const navigate = useNavigate();

    const { cycleEvaluationId } = useParams<CycleEvaluationFormNavigatorParams>();

    const {
        data: cycleEvaluation,
        isLoading: isCycleEvaluationLoading,
        isError: isCycleEvaluationError,
    } = useGetCycleEvaluation(cycleEvaluationId);

    const { mutateAsync: updateSchedule, isPending: isUpdatingSchedule } = useUpdateCycleSchedule();
    const { mutateAsync: launchEvaluation, isPending: isLaunching } = useLaunchCycleEvaluation();
    const { mutateAsync: sendTestEmail, isPending: isSendingTestEmail } =
        useSendCycleEvaluationTestEmail();

    const { cycleSchedule } = cycleEvaluation || {};

    const availableLanguageCodes = useMemo(() => Object.keys(getAvailableLanguages()), []);

    const { watch, setValue, reset, trigger, getFieldState } = useForm<ScheduleCreationFormData>({
        resolver: zodResolver(scheduleCreationSchema),
        defaultValues: {
            [ScheduleCreationFormFields.scheduledAt]: new Date(),
            [ScheduleCreationFormFields.feedbackFrequency]: Frequency.UPON_REQUEST,
            [ScheduleCreationFormFields.reminderSevenDays]: false,
            [ScheduleCreationFormFields.reminderThreeDays]: false,
            [ScheduleCreationFormFields.reminderOnDeadline]: false,
            [ScheduleCreationFormFields.managerViewScores]: true,
            [ScheduleCreationFormFields.managerReadComments]: true,
            [ScheduleCreationFormFields.managerAccessIndividual]: true,
            [ScheduleCreationFormFields.emailLanguage]: normalizeLanguageCode(i18n.language),
            [ScheduleCreationFormFields.emailTitle]: '',
            [ScheduleCreationFormFields.emailMessage]: getEmptyEmailMessage(),
        },
    });

    const formValues = watch();

    const [emailTitleByLanguage, setEmailTitleByLanguage] = useState<LocalizedText>(() =>
        availableLanguageCodes.reduce(
            (acc, languageCode) => ({
                ...acc,
                [languageCode]: '',
            }),
            {} as LocalizedText,
        ),
    );

    const [emailMessageByLanguage, setEmailMessageByLanguage] = useState<
        Record<string, Descendant[]>
    >(() =>
        availableLanguageCodes.reduce(
            (acc, languageCode) => ({
                ...acc,
                [languageCode]: getEmptyEmailMessage(),
            }),
            {} as Record<string, Descendant[]>,
        ),
    );
    const [customizedLanguages, setCustomizedLanguages] = useState<Set<string>>(new Set());

    const currentUser = useAtomValue(userInstanceAtom);
    const [testEmailAddress, setTestEmailAddress] = useState<string>(currentUser.email || '');
    const [isFormInitialized, setIsFormInitialized] = useState(false);
    const [hasUserEditedScheduledAt, setHasUserEditedScheduledAt] = useState(false);

    const getEmailDefaults = useCallback(
        (lang: string) => getManagerNotificationEmailDefaults(lang),
        [],
    );

    const isDefaultTitle = useCallback((value: string) => {
        return MANAGER_NOTIFICATION_DEFAULT_TITLES.has(value);
    }, []);

    const isDefaultBody = useCallback((value: string) => {
        return MANAGER_NOTIFICATION_DEFAULT_BODIES.has(value);
    }, []);

    useEffect(() => {
        const parseLocalizedField = (
            value: string | MultilingualLabel | null | undefined,
        ): LocalizedText => {
            if (value === null || value === undefined || typeof value === 'string') {
                return {};
            }
            return value as unknown as LocalizedText;
        };

        if (cycleSchedule && cycleEvaluation?.reviewType !== undefined) {
            const emailLanguage = normalizeLanguageCode(i18n.language);
            const currentPayloadLanguage = emailLanguage;

            const localizedEmailTitles = parseLocalizedField(cycleSchedule.emailTitle);
            const localizedEmailMessages = parseLocalizedField(cycleSchedule.emailMessage);
            const customized = new Set<string>();

            const storedTitle =
                typeof cycleSchedule.emailTitle === 'string' ? cycleSchedule.emailTitle : null;
            const storedMessage =
                typeof cycleSchedule.emailMessage === 'string' ? cycleSchedule.emailMessage : null;

            const emailTitleByLanguageValue: LocalizedText =
                storedTitle !== null
                    ? {
                          [currentPayloadLanguage]:
                              storedTitle && !isDefaultTitle(storedTitle)
                                  ? storedTitle
                                  : getEmailDefaults(currentPayloadLanguage).title,
                      }
                    : Object.fromEntries(
                          Object.keys(localizedEmailTitles).map((lang) => {
                              const stored = localizedEmailTitles[lang];
                              const defaultTitle = getEmailDefaults(lang).title;
                              const storedMsg = localizedEmailMessages[lang];
                              const defaultBody = getEmailDefaults(lang).body;
                              const isTitleCustomized =
                                  stored && stored !== defaultTitle && !isDefaultTitle(stored);
                              const isBodyCustomized =
                                  storedMsg &&
                                  storedMsg !== defaultBody &&
                                  !isDefaultBody(storedMsg);
                              if (isTitleCustomized || isBodyCustomized) {
                                  customized.add(lang);
                              }
                              return [lang, isTitleCustomized ? stored : defaultTitle];
                          }),
                      );
            const emailMessageByLanguageValue: Record<string, Descendant[]> =
                storedMessage !== null
                    ? {
                          [currentPayloadLanguage]: htmlToSlate(
                              storedMessage && !isDefaultBody(storedMessage)
                                  ? storedMessage
                                  : getEmailDefaults(currentPayloadLanguage).body,
                          ),
                      }
                    : Object.entries(localizedEmailMessages).reduce(
                          (acc, [key, value]) => ({
                              ...acc,
                              [key]: htmlToSlate(
                                  value && !isDefaultBody(value)
                                      ? value
                                      : getEmailDefaults(key).body,
                              ),
                          }),
                          {} as Record<string, Descendant[]>,
                      );

            setCustomizedLanguages(customized);

            const allLanguagesTitleDefaults = availableLanguageCodes.reduce(
                (acc, lang) => ({
                    ...acc,
                    [lang]: emailTitleByLanguageValue[lang] || getEmailDefaults(lang).title,
                }),
                {} as LocalizedText,
            );
            const allLanguagesMessageDefaults = availableLanguageCodes.reduce(
                (acc, lang) => ({
                    ...acc,
                    [lang]:
                        emailMessageByLanguageValue[lang] ??
                        htmlToSlate(getEmailDefaults(lang).body),
                }),
                {} as Record<string, Descendant[]>,
            );

            setEmailTitleByLanguage(allLanguagesTitleDefaults);
            setEmailMessageByLanguage(allLanguagesMessageDefaults);

            reset({
                [ScheduleCreationFormFields.scheduledAt]: cycleSchedule.scheduledAt
                    ? new Date(cycleSchedule.scheduledAt)
                    : new Date(),
                [ScheduleCreationFormFields.feedbackFrequency]: cycleSchedule.frequency,
                [ScheduleCreationFormFields.reminderSevenDays]: isScheduleReminderChecked({
                    scheduleReminder: cycleSchedule.scheduleReminders,
                    daysBeforeDeadline: 7,
                }),
                [ScheduleCreationFormFields.reminderThreeDays]: isScheduleReminderChecked({
                    scheduleReminder: cycleSchedule.scheduleReminders,
                    daysBeforeDeadline: 3,
                }),
                [ScheduleCreationFormFields.reminderOnDeadline]: isScheduleReminderChecked({
                    scheduleReminder: cycleSchedule.scheduleReminders,
                    daysBeforeDeadline: 0,
                }),
                [ScheduleCreationFormFields.managerViewScores]: true,
                [ScheduleCreationFormFields.managerReadComments]: true,
                [ScheduleCreationFormFields.managerAccessIndividual]: true,
                [ScheduleCreationFormFields.emailLanguage]: emailLanguage,
                [ScheduleCreationFormFields.emailTitle]:
                    emailTitleByLanguageValue[currentPayloadLanguage] ??
                    getEmailDefaults(currentPayloadLanguage).title,
                [ScheduleCreationFormFields.emailMessage]:
                    emailMessageByLanguageValue[currentPayloadLanguage] ??
                    htmlToSlate(getEmailDefaults(currentPayloadLanguage).body),
            });
            setIsFormInitialized(true);
        }
    }, [
        cycleSchedule,
        cycleEvaluation?.reviewType,
        reset,
        getEmailDefaults,
        isDefaultTitle,
        isDefaultBody,
        availableLanguageCodes,
    ]);

    // Deliberately scoped to cycleEvaluationId only: also depending on cycleSchedule would reset
    // this flag on every Save and let an already-saved past date slip back through as untouched.
    useEffect(() => {
        setHasUserEditedScheduledAt(false);
    }, [cycleEvaluationId]);

    const managerAccessList: ListItemData[] = useMemo(
        () => [
            {
                content: t('evaluation360ScheduleCreation.resultsVisibility.viewAllScore'),
                icon: (
                    <Icon
                        name={'essential-checkmark'}
                        color={Colors.PRIMARY}
                    />
                ),
            },
            {
                content: t('evaluation360ScheduleCreation.resultsVisibility.readAllComments'),
                icon: (
                    <Icon
                        name={'essential-checkmark'}
                        color={Colors.PRIMARY}
                    />
                ),
            },
            {
                content: t('evaluation360ScheduleCreation.resultsVisibility.accessIndividual'),
                icon: (
                    <Icon
                        name={'essential-checkmark'}
                        color={Colors.PRIMARY}
                    />
                ),
            },
        ],
        [t],
    );

    const validateScheduleForm = useCallback(async (): Promise<boolean> => {
        const isFormValid = await trigger([
            ScheduleCreationFormFields.feedbackFrequency,
            ScheduleCreationFormFields.scheduledAt,
            ScheduleCreationFormFields.emailTitle,
            ScheduleCreationFormFields.emailMessage,
        ]);

        if (!isFormValid) {
            const feedbackFrequency = getFieldState(ScheduleCreationFormFields.feedbackFrequency);
            const scheduledAt = getFieldState(ScheduleCreationFormFields.scheduledAt);
            const titleState = getFieldState(ScheduleCreationFormFields.emailTitle);
            const messageState = getFieldState(ScheduleCreationFormFields.emailMessage);

            const errorMessage =
                validationMessageKey(feedbackFrequency.error?.message) ||
                validationMessageKey(scheduledAt.error?.message) ||
                validationMessageKey(titleState.error?.message) ||
                validationMessageKey(messageState.error?.message);

            showWarningToast({
                title: t('errors.genericTitle'),
                message: errorMessage ? t(errorMessage) : '',
            });

            return false;
        }

        return true;
    }, [trigger, getFieldState, t]);

    const isScheduledAtInPastByMinute = useCallback((scheduledAt: Date): boolean => {
        const scheduledAtByMinute = new Date(scheduledAt);
        const nowByMinute = new Date();

        scheduledAtByMinute.setSeconds(0, 0);
        nowByMinute.setSeconds(0, 0);

        return scheduledAtByMinute < nowByMinute;
    }, []);

    const saveSchedule = useCallback(
        async (overrideScheduledAt?: Date) => {
            if (!cycleSchedule) {
                return;
            }

            const scheduledAt = overrideScheduledAt ?? formValues.scheduledAt;

            const scheduleReminders = mergeScheduleReminders({
                storedReminders: cycleSchedule.scheduleReminders,
                reminderSevenDays: formValues.reminderSevenDays,
                reminderThreeDays: formValues.reminderThreeDays,
                reminderOnDeadline: formValues.reminderOnDeadline,
            });

            const currentPayloadLanguage = formValues.emailLanguage;
            const allCustomized = new Set(customizedLanguages).add(currentPayloadLanguage);
            const mergedTitles = {
                ...emailTitleByLanguage,
                [currentPayloadLanguage]: formValues.emailTitle,
            };
            const mergedMessages = {
                ...emailMessageByLanguage,
                [currentPayloadLanguage]: formValues.emailMessage,
            };

            const customEmailTitle = Object.fromEntries(
                [...allCustomized]
                    .filter((lang) => mergedTitles[lang] !== undefined)
                    .map((lang) => [lang, mergedTitles[lang]]),
            );
            const customEmailMessage = Object.fromEntries(
                [...allCustomized]
                    .filter((lang) => mergedMessages[lang] !== undefined)
                    .map((lang) => [lang, slateToHtml(mergedMessages[lang])]),
            );

            await updateSchedule({
                ...cycleSchedule,
                frequency: formValues.feedbackFrequency as typeof cycleSchedule.frequency,
                scheduledAt: scheduledAt?.toISOString() ?? cycleSchedule.scheduledAt,
                emailTitle: customEmailTitle as unknown as MultilingualLabel,
                emailMessage: customEmailMessage as unknown as MultilingualLabel,
                scheduleReminders,
            });
        },
        [
            cycleSchedule,
            formValues,
            updateSchedule,
            emailTitleByLanguage,
            emailMessageByLanguage,
            customizedLanguages,
        ],
    );

    const onSaveSettings = useCallback(async () => {
        const isValid = await validateScheduleForm();
        if (!isValid) {
            return;
        }

        try {
            await saveSchedule();
            showSuccessToast({
                title: t('cycleEvaluationSchedule.saveSettingsSuccessTitle'),
                message: t('cycleEvaluationSchedule.saveSettingsSuccessMessage'),
            });
        } catch (error) {
            handleError({ error });
        }
    }, [validateScheduleForm, saveSchedule, t, handleError]);

    const onTestEmail = useCallback(async () => {
        const isTitleValid = await trigger(ScheduleCreationFormFields.emailTitle);
        const isMessageValid = await trigger(ScheduleCreationFormFields.emailMessage);

        if (!isTitleValid || !isMessageValid) {
            const titleState = getFieldState(ScheduleCreationFormFields.emailTitle);
            const messageState = getFieldState(ScheduleCreationFormFields.emailMessage);

            const titleErrorMessage = validationMessageKey(titleState.error?.message);
            const messageErrorMessage = validationMessageKey(messageState.error?.message);
            const errorMessage = titleErrorMessage || messageErrorMessage;

            showWarningToast({
                title: t('errors.genericTitle'),
                message: errorMessage ? t(errorMessage) : '',
            });
            return;
        }

        try {
            await sendTestEmail({
                emailTitle: formValues.emailTitle ?? '',
                emailMessage: slateToHtml(formValues.emailMessage!),
                cycleEvaluationId: cycleEvaluationId || '',
                emailAddress: testEmailAddress,
                selectedLang: formValues.emailLanguage,
            });

            showSuccessToast({
                title: t('evaluation360ScheduleCreation.testEmail.successTitle'),
                message: t('evaluation360ScheduleCreation.testEmail.successMessage'),
            });
        } catch (error) {
            handleError({ error });
        }
    }, [
        trigger,
        getFieldState,
        t,
        sendTestEmail,
        cycleEvaluationId,
        formValues.emailTitle,
        formValues.emailMessage,
        formValues.emailLanguage,
        testEmailAddress,
        handleError,
    ]);

    const onScheduledAtChange = (date: Date) => {
        setValue(ScheduleCreationFormFields.scheduledAt, date);
        setHasUserEditedScheduledAt(true);
    };

    const onFeedbackFrequencyChange = (value: string) => {
        setValue(ScheduleCreationFormFields.feedbackFrequency, value);
    };

    const onReminderChange = useCallback(
        ({ field, value }: { field: keyof typeof ScheduleCreationFormFields; value: boolean }) => {
            setValue(field as keyof ScheduleCreationFormData, value);
        },
        [setValue],
    );

    const onEmailLanguageChange = useCallback(
        (value: string) => {
            const currentPayloadLanguage = formValues.emailLanguage;
            const selectedPayloadLanguage = value;

            setEmailTitleByLanguage((previous) => ({
                ...previous,
                [currentPayloadLanguage]: formValues.emailTitle,
            }));
            setEmailMessageByLanguage((previous) => ({
                ...previous,
                [currentPayloadLanguage]: formValues.emailMessage,
            }));

            setValue(ScheduleCreationFormFields.emailLanguage, value);
            setValue(
                ScheduleCreationFormFields.emailTitle,
                emailTitleByLanguage[selectedPayloadLanguage] ??
                    getEmailDefaults(selectedPayloadLanguage).title,
            );
            setValue(
                ScheduleCreationFormFields.emailMessage,
                emailMessageByLanguage[selectedPayloadLanguage] ??
                    htmlToSlate(getEmailDefaults(selectedPayloadLanguage).body),
                { shouldValidate: true },
            );
        },
        [
            formValues,
            setValue,
            emailTitleByLanguage,
            emailMessageByLanguage,
            getEmailDefaults,
            cycleEvaluation?.reviewType,
        ],
    );

    const onEmailTitleChange = useCallback(
        (value: string) => {
            setValue(ScheduleCreationFormFields.emailTitle, value);
            const currentPayloadLanguage = formValues.emailLanguage;
            setEmailTitleByLanguage((previous) => ({
                ...previous,
                [currentPayloadLanguage]: value,
            }));
            setCustomizedLanguages((previous) => new Set(previous).add(currentPayloadLanguage));
        },
        [setValue, formValues.emailLanguage],
    );

    const onEmailMessageChange = useCallback(
        (value: Descendant[]) => {
            setValue(ScheduleCreationFormFields.emailMessage, value, { shouldValidate: true });
            const currentPayloadLanguage = formValues.emailLanguage;
            setEmailMessageByLanguage((previous) => ({
                ...previous,
                [currentPayloadLanguage]: value,
            }));
            setCustomizedLanguages((previous) => new Set(previous).add(currentPayloadLanguage));
        },
        [setValue, formValues.emailLanguage],
    );

    const onLaunch = useCallback(async () => {
        if (!cycleEvaluation) {
            handleError({});
            return false;
        }

        const isAlreadyActive = cycleEvaluation.status === CycleEvaluationStatus.ACTIVE;
        const isScheduledAtStale =
            !isAlreadyActive &&
            !!formValues.scheduledAt &&
            isScheduledAtInPastByMinute(formValues.scheduledAt);

        if (isScheduledAtStale && hasUserEditedScheduledAt) {
            showErrorToast({
                title: t('cycleEvaluationSchedule.validation.scheduledAtPastTitle'),
                message: t('cycleEvaluationSchedule.validation.scheduledAtPast'),
                action: {
                    label: t('cycleEvaluationSchedule.validation.resetToTodayAction'),
                    onClick: () => setValue(ScheduleCreationFormFields.scheduledAt, new Date()),
                },
            });

            return false;
        }

        const effectiveScheduledAt = isScheduledAtStale ? new Date() : formValues.scheduledAt;

        if (isScheduledAtStale) {
            setValue(ScheduleCreationFormFields.scheduledAt, effectiveScheduledAt as Date);
        }

        const isValid = await validateScheduleForm();
        if (!isValid) {
            return false;
        }

        const hasContent = hasQuestionnaireContent({
            questionnaire: cycleEvaluation.cycleQuestionnaire,
            reviewType: cycleEvaluation.reviewType,
        });

        if (!hasContent) {
            showWarningToast({
                message: t('evaluation360Questionnaire.message.noQuestions'),
            });
            return false;
        }
        try {
            await saveSchedule(effectiveScheduledAt);
            await launchEvaluation(cycleEvaluation);

            showSuccessToast({
                title: t('cycleEvaluationSchedule.saveSettingsSuccessTitle'),
                message: t('cycleEvaluationSchedule.saveSettingsSuccessMessage'),
            });

            navigate({
                path: RouteName.SuccessMessage,
                state: {
                    title: t('cycleEvaluationSuccessMessage.title'),
                    buttonLabel: t('cycleEvaluationSuccessMessage.goToDashboard'),
                    actionType: SuccessMessageAction.NAVIGATE_TO_CYCLE_EVAL_DASHBOARD,
                },
            });
            return true;
        } catch (error) {
            handleError({ error });
            return false;
        }
    }, [
        validateScheduleForm,
        launchEvaluation,
        cycleEvaluation,
        navigate,
        handleError,
        saveSchedule,
        setValue,
        formValues.scheduledAt,
        hasUserEditedScheduledAt,
        isScheduledAtInPastByMinute,
        t,
    ]);

    useFetchErrorsHandler({
        errors: [isCycleEvaluationError],
    });

    useRegisterPageAction(scheduleCreationOnNextAtom, onLaunch);

    const isAlreadyLaunched = cycleEvaluation?.status === CycleEvaluationStatus.ACTIVE;

    const launchTranslationKeys = useMemo<LaunchReviewTranslationKeys>(
        () =>
            isAlreadyLaunched
                ? {
                      titleKey: 'cycleEvaluationSchedule.updateReview.title',
                      ctaKey: 'cycleEvaluationSchedule.updateReview.launch',
                  }
                : {
                      titleKey: 'cycleEvaluationSchedule.launchReview.title',
                      ctaKey: 'cycleEvaluationSchedule.launchReview.launch',
                  },
        [isAlreadyLaunched],
    );

    return (
        <ScheduleCreation
            title={t('cycleEvaluationSchedule.title')}
            subtitle={t('cycleEvaluationSchedule.subtitle')}
            reviewees={[]}
            frequencyOptions={getFrequencyOptions()}
            managerAccessList={managerAccessList}
            formValues={formValues}
            isSendingTestEmail={isSendingTestEmail}
            isLaunching={isLaunching}
            scheduleFrequency={true}
            scheduledAt={formValues.scheduledAt}
            feedbackFrequency={formValues.feedbackFrequency}
            editorKey={`${cycleSchedule?.id ?? 'cycle-schedule'}-${formValues.emailLanguage}`}
            isLoading={!!cycleEvaluationId && (isCycleEvaluationLoading || !isFormInitialized)}
            isSavingSchedule={isUpdatingSchedule}
            onSaveSettings={onSaveSettings}
            onLaunchReview={onLaunch}
            onScheduledAtChange={onScheduledAtChange}
            onFeedbackFrequencyChange={onFeedbackFrequencyChange}
            onReminderChange={onReminderChange}
            testEmailAddress={testEmailAddress}
            onEmailLanguageChange={onEmailLanguageChange}
            onEmailTitleChange={onEmailTitleChange}
            onEmailMessageChange={onEmailMessageChange}
            onTestEmailAddressChange={setTestEmailAddress}
            onTestEmail={onTestEmail}
            launchTranslationKeys={launchTranslationKeys}
            customizeSurveyEmailHeader={t('cycleEvaluationSchedule.customizeSurveyEmail.title')}
        />
    );
};

export { ScheduleCreationPage };
